import { useEffect, useState, type ReactElement } from 'react'
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded'
import {
  listFamilyEntitlements,
  listFamilyBookings,
  type EduEntitlement,
  type EduBooking,
} from '../../services/eduBookingService'
import { FamilyCard, Button, StatusChip, MiniLoader } from '../Common'
import { useNls } from '../../nls/nlsProvider'
import { formatCalendarDate, formatWallClockTime } from '../../utils/eduFamilyFormat'
import './familyBookingsPanel.css'

export interface FamilyBookingsPanelProps {
  workspaceId: string
  learnerId: string
  frozen?: boolean
  onBack?: () => void
}

export const FamilyBookingsPanel = ({
  workspaceId,
  learnerId,
  frozen = false,
  onBack,
}: FamilyBookingsPanelProps): ReactElement => {
  const { t, locale } = useNls()

  const queryKey = `${workspaceId}_${learnerId}`
  const [fetchState, setFetchState] = useState<{
    key: string
    entitlements: EduEntitlement[]
    bookings: EduBooking[]
    error: string | null
  }>({
    key: '',
    entitlements: [],
    bookings: [],
    error: null,
  })

  useEffect(() => {
    if (frozen) return
    let active = true

    Promise.all([
      listFamilyEntitlements({ workspaceId, learnerId }),
      listFamilyBookings({ workspaceId, learnerId }),
    ])
      .then(([entRes, bookRes]) => {
        if (!active) return
        setFetchState({
          key: queryKey,
          entitlements: entRes.entitlements || [],
          bookings: bookRes.bookings || [],
          error: null,
        })
      })
      .catch((err: unknown) => {
        if (!active) return
        const msg = err instanceof Error ? err.message : 'Failed to load bookings data.'
        setFetchState({
          key: queryKey,
          entitlements: [],
          bookings: [],
          error: msg,
        })
      })

    return () => {
      active = false
    }
  }, [workspaceId, learnerId, frozen, queryKey])

  const loading = !frozen && fetchState.key !== queryKey
  const entitlements = fetchState.key === queryKey ? fetchState.entitlements : []
  const bookings = fetchState.key === queryKey ? fetchState.bookings : []
  const error = fetchState.key === queryKey ? fetchState.error : null

  if (frozen) {
    return (
      <div className="familyBookingsPanel">
        {onBack && (
          <div className="familyBookingsPanel__nav">
            <Button
              variant="tertiary"
              size="sm"
              onClick={onBack}
              icon={<ArrowBackRoundedIcon />}
            >
              {t('NLSCommonActionBack')}
            </Button>
          </div>
        )}
        <FamilyCard title={t('NLSFamilyNoticeHeading')}>
          <div className="familyBookingsPanel__frozen">{t('NLSFamilyFrozenNotice')}</div>
        </FamilyCard>
      </div>
    )
  }

  // Filter history bookings
  const historyBookings = bookings.filter((b) =>
    ['ATTENDED', 'NO_SHOW', 'LATE_CANCEL', 'CANCELLED'].includes(b.status),
  )

  const historyTone = (status: string) => {
    switch (status) {
      case 'ATTENDED':
        return 'success'
      case 'NO_SHOW':
        return 'danger'
      case 'LATE_CANCEL':
        return 'warning'
      case 'CANCELLED':
        return 'neutral'
      default:
        return 'neutral'
    }
  }

  const historyLabel = (status: string) => {
    switch (status) {
      case 'ATTENDED':
        return t('NLSFamilyBookingsStatusAttended')
      case 'NO_SHOW':
        return t('NLSFamilyBookingsStatusNoShow')
      case 'LATE_CANCEL':
        return t('NLSFamilyBookingsStatusLateCancel')
      case 'CANCELLED':
        return t('NLSFamilyBookingsStatusCancelled')
      default:
        return status
    }
  }

  return (
    <div className="familyBookingsPanel">
      {onBack && (
        <div className="familyBookingsPanel__nav">
          <Button
            variant="tertiary"
            size="sm"
            onClick={onBack}
            icon={<ArrowBackRoundedIcon />}
          >
            {t('NLSCommonActionBack')}
          </Button>
        </div>
      )}

      {loading ? (
        <div className="familyBookingsPanel__loader" data-testid="family-bookings-loader">
          <MiniLoader />
        </div>
      ) : error ? (
        <FamilyCard title={t('NLSFamilyNoticeHeading')}>
          <div className="familyBookingsPanel__empty">{error}</div>
        </FamilyCard>
      ) : (
        <>
          {/* Section 1: Packs & Memberships */}
          <FamilyCard title={t('NLSFamilyBookingsEntitlementsHeading')}>
            {entitlements.length === 0 ? (
              <div className="familyBookingsPanel__empty">
                {t('NLSFamilyBookingsNoEntitlements')}
              </div>
            ) : (
              <div className="familyBookingsPanel__list">
                {entitlements.map((ent) => {
                  const used =
                    (ent.sessionsTotal ?? 0) -
                    (ent.sessionsRemaining ?? 0) -
                    (ent.sessionsHeld ?? 0)

                  return (
                    <div
                      key={ent.id}
                      className="familyBookingsPanel__item"
                      data-testid={`entitlement-row-${ent.id}`}
                    >
                      <div className="familyBookingsPanel__itemMeta">
                        <div className="familyBookingsPanel__itemTitle">
                          {ent.planTitle || ent.kind}
                        </div>
                        {ent.kind === 'MEMBERSHIP' ? (
                          <div className="familyBookingsPanel__itemSubtitle">
                            {t('NLSFamilyBookingsMembershipCap', {
                              count: ent.capCount ?? 0,
                              period: ent.capPer === 'WEEK' ? 'week' : 'month',
                            })}
                          </div>
                        ) : (
                          <div className="familyBookingsPanel__itemSubtitle">
                            {t('NLSFamilyBookingsSessionsLeft', {
                              left: ent.sessionsRemaining ?? 0,
                              used: Math.max(0, used),
                              held: ent.sessionsHeld ?? 0,
                              total: ent.sessionsTotal ?? 0,
                            })}
                          </div>
                        )}
                        {(ent.validFrom || ent.validTo) && (
                          <div className="familyBookingsPanel__itemDates">
                            {t('NLSFamilyBookingsValidDates', {
                              from: formatCalendarDate(ent.validFrom || '', locale),
                              to: formatCalendarDate(ent.validTo || '', locale),
                            })}
                          </div>
                        )}
                      </div>
                      <div className="familyBookingsPanel__itemStatus">
                        {ent.frozenUntil ? (
                          <StatusChip tone="warning" size="sm">
                            {t('NLSFamilyBookingsFrozenBadge', {
                              to: formatCalendarDate(ent.frozenUntil, locale),
                            })}
                          </StatusChip>
                        ) : ent.status === 'ACTIVE' ? (
                          <StatusChip tone="success" size="sm">
                            {t('NLSFamilyBookingsStatusActive')}
                          </StatusChip>
                        ) : ent.status === 'EXPIRED' ? (
                          <StatusChip tone="neutral" size="sm">
                            {t('NLSFamilyBookingsStatusExpired')}
                          </StatusChip>
                        ) : (
                          <StatusChip tone="neutral" size="sm">
                            {ent.status}
                          </StatusChip>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </FamilyCard>

          {/* Section 2: Booking History */}
          <FamilyCard title={t('NLSFamilyBookingsHistoryHeading')}>
            {historyBookings.length === 0 ? (
              <div className="familyBookingsPanel__empty">
                {t('NLSFamilyBookingsNoHistory')}
              </div>
            ) : (
              <div className="familyBookingsPanel__list">
                {historyBookings.map((b) => (
                  <div
                    key={b.id}
                    className="familyBookingsPanel__item"
                    data-testid={`history-row-${b.id}`}
                  >
                    <div className="familyBookingsPanel__itemMeta">
                      <div className="familyBookingsPanel__itemTitle">
                        {b.title || b.seriesId}
                      </div>
                      <div className="familyBookingsPanel__itemDates">
                        {formatCalendarDate(b.date, locale)}
                        {b.startTime ? ` · ${formatWallClockTime(b.startTime, locale)}` : ''}
                      </div>
                    </div>
                    <div className="familyBookingsPanel__itemStatus">
                      <StatusChip tone={historyTone(b.status)} size="sm">
                        {historyLabel(b.status)}
                      </StatusChip>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </FamilyCard>

          {/* Section 3: Drop-in Note */}
          <div className="familyBookingsPanel__dropInNote">
            {t('NLSFamilyBookingsDropInNote')}
          </div>
        </>
      )}
    </div>
  )
}
